import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { Icon } from "../components/icons";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useAppContext } from "../hooks/useAppContext";

/**
 * Logs — the tamper-evident audit trail of every action ODIN takes. Layout uses
 * the ODIN design system; the toolbar and table are driven by real data with
 * honest empty states until a server is connected. No fabricated log entries.
 */

const COLUMNS = ["Time", "Action", "Actor", "Result"] as const;

export function Logs(): JSX.Element {
  const status = usePlatformStatus();
  const { guildName } = useAppContext();
  const ready = status.kind === "ready" ? status.data : null;
  const connected = Boolean(guildName);

  return (
    <div className="ax-content" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Logs</h1>
            <p className="ax-head__sub">A tamper-evident audit trail of every action ODIN takes on your server.</p>
          </div>
        </div>

        {status.kind === "loading" ? <LoadingState label="Contacting the control API…" /> : null}
        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {status.kind === "unavailable" ? <ApiUnavailableState detail={status.detail} /> : null}

        {ready ? (
          <>
            <div className="ax-toolbar">
              <span className="ax-input ax-input--disabled" aria-disabled="true">
                <Icon.search />
                Search logs…
              </span>
              <span className="ax-btn ax-btn--secondary" aria-disabled="true" style={{ opacity: 0.6, cursor: "not-allowed" }}>
                All types
              </span>
              <span className="ax-btn ax-btn--secondary" aria-disabled="true" style={{ opacity: 0.6, cursor: "not-allowed" }}>
                Last 24h
              </span>
            </div>

            <div className="ax-table-wrap">
              <table className="ax-table">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="ax-table__empty" colSpan={COLUMNS.length}>
                      {connected
                        ? "No log entries yet."
                        : "Connect a Discord server to load logs."}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="ax-metric__foot">Every action ODIN takes is recorded here — tamper-evident.</p>
          </>
        ) : null}
      </div>
    </div>
  );
}
